import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Loader2, AlertCircle, ArrowRight, Zap } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useBankBalance } from '../hooks/useBankBalance';
import { BankBalanceFilters } from '../components/bank-balance/BankBalanceFilters';
import { BankBalanceSummary } from '../components/bank-balance/BankBalanceSummary';
import { BankBalanceToolbar } from '../components/bank-balance/BankBalanceToolbar';
import { BankBalanceTable } from '../components/bank-balance/BankBalanceTable';
import { BankAccountsTab } from '../components/bank-balance/BankAccountsTab';
import { useToast } from '../components/ui/Toast';
import { OverviewTab } from '../components/bank-balance/OverviewTab';
import { CopyEntriesModal } from '../components/bank-balance/CopyEntriesModal';
import PlannedEventsEditor from '../components/bank-balance/PlannedEventsEditor';
import CashOutlook from '../components/bank-balance/CashOutlook';
import { ArApBalanceTab } from '../components/bank-balance/ArApBalanceTab';
import { isNonBcCompany, type CreateEntryData } from '../types/bankBalance';

type TabId = 'entries' | 'arap' | 'outlook' | 'overview' | 'accounts';

export default function BankBalancePage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/bank-balance');
    const { user } = useAuth();
    const { toast } = useToast();
    const isAdmin = user?.isAdmin ?? false;

    const {
        year, setYear, month, setMonth,
        companyCode, setCompanyCode, resolvedCompany,
        entries, bankAccounts, summary, comparison, syncCheck,
        loading, error,
        createEntry, updateEntry, deleteEntry, copyEntries,
        refreshSyncCheck,
    } = useBankBalance();

    const [activeTab, setActiveTab] = useState<TabId>('entries');
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [outlookRefreshKey, setOutlookRefreshKey] = useState(0);

    // Get unique dates for copy-from dropdown
    const availableDates = useMemo(() => {
        const dates = new Set(
            entries
                .filter(e => !e.IsAutomatic)
                .map(e => e.PostingDate.split('T')[0])
        );
        return Array.from(dates).sort().reverse();
    }, [entries]);

    const activeCompanyForTabs = (companyCode || resolvedCompany) as string | null;
    const showArApTabForGate = isNonBcCompany(activeCompanyForTabs);

    // Auto-switch away from the AR/AP tab when user selects a BC company.
    // Must run BEFORE any conditional early returns so hook order stays stable.
    useEffect(() => {
        if (activeTab === 'arap' && !showArApTabForGate) setActiveTab('entries');
    }, [activeTab, showArApTabForGate]);

    if (permLoading || loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    const handleNewEntry = async () => {
        if (!resolvedCompany) return;
        if (bankAccounts.length === 0) {
            toast('No bank accounts configured for this company yet. Add one on the "Bank Accounts" tab first.', 'error');
            return;
        }

        // Smart defaults
        const now = new Date();
        const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
        const defaultDate = isCurrentMonth
            ? now.toISOString().split('T')[0]
            : new Date(year, month, 0).toISOString().split('T')[0]; // last day of month

        const lastUsedAccount = bankAccounts[0];

        const data: CreateEntryData = {
            companyCode: resolvedCompany,
            bankId: lastUsedAccount.BankID,
            postingDate: defaultDate,
            currencyCode: lastUsedAccount.CurrencyCode,
            amountFCY: 0,
        };

        await createEntry(data);
    };

    const handleCopy = async (sourceDate: string, targetDate: string) => {
        if (!resolvedCompany) return;
        await copyEntries({
            companyCode: resolvedCompany,
            sourceDate,
            targetDate,
        });
    };

    const handleExport = () => {
        // Simple CSV export
        const header = 'Date,Bank Account,Amount FCY,Currency,Changed By\n';
        const rows = entries.map(e =>
            `${new Date(e.PostingDate).toLocaleDateString('de-CH')},${e.BankAccountName || e.BankID},${e.AmountFCY},${e.CurrencyCode},${e.UpdatedBy || e.CreatedBy}`
        ).join('\n');

        const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bank-balance-${resolvedCompany}-${year}-${String(month).padStart(2, '0')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const activeCompany = activeCompanyForTabs;
    const showArApTab = showArApTabForGate;

    const tabs: { id: TabId; label: string }[] = [
        { id: 'entries', label: 'Balance Entry' },
        ...(showArApTab ? [{ id: 'arap' as TabId, label: 'AR / AP Balance' }] : []),
        { id: 'outlook', label: 'Planned Events & Cash Outlook' },
        { id: 'overview', label: 'Overview' },
        { id: 'accounts', label: 'Bank Accounts' },
    ];

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3 min-w-0">
                    <Landmark className="w-7 h-7 text-blue-600 flex-shrink-0" />
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Bank Balance Input</h1>
                </div>
                {canEdit && (
                    <Link
                        to="/bank-balance/weekly"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
                    >
                        <Zap className="w-4 h-4" /> Quick entry
                    </Link>
                )}
            </div>

            {/* Sync check banner — admins only: classifying accounts is admin work (Roman, 2026-10-08) */}
            {isAdmin && syncCheck && syncCheck.unclassifiedCount > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 text-amber-700 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>
                            <strong>{syncCheck.unclassifiedCount} new bank account{syncCheck.unclassifiedCount > 1 ? 's' : ''}</strong> detected — not yet classified.
                        </span>
                    </div>
                    <button
                        onClick={() => setActiveTab('accounts')}
                        className="flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-900"
                    >
                        Go to Bank Accounts <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                            activeTab === tab.id
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'entries' && (
                <div className="space-y-4">
                    {/* Filters + Summary */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <BankBalanceFilters
                            year={year}
                            month={month}
                            companyCode={companyCode}
                            resolvedCompany={resolvedCompany}
                            onYearChange={setYear}
                            onMonthChange={setMonth}
                            onCompanyChange={setCompanyCode}
                            isAdmin={isAdmin}
                        />
                        <BankBalanceSummary summary={summary} />
                    </div>

                    {/* Toolbar */}
                    <BankBalanceToolbar
                        canEdit={canEdit}
                        hasChanges={false}
                        saving={false}
                        onNewEntry={handleNewEntry}
                        onCopyFromDate={() => setShowCopyModal(true)}
                        onExport={handleExport}
                        onSaveAll={() => {}}
                        onUndoAll={() => {}}
                    />

                    {/* Table */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <BankBalanceTable
                            entries={entries}
                            bankAccounts={bankAccounts}
                            comparison={comparison}
                            canEdit={canEdit}
                            onUpdate={updateEntry}
                            onDelete={deleteEntry}
                        />
                    </div>
                </div>
            )}

            {activeTab === 'arap' && activeCompany && showArApTab && (
                <ArApBalanceTab
                    companyCode={activeCompany}
                    canEdit={canEdit}
                />
            )}

            {activeTab === 'outlook' && (companyCode || resolvedCompany) && (
                <div className="space-y-4">
                    <PlannedEventsEditor
                        companyCode={(companyCode || resolvedCompany) as string}
                        canEdit={canEdit}
                        onChange={() => setOutlookRefreshKey(k => k + 1)}
                    />
                    <CashOutlook
                        companyCode={(companyCode || resolvedCompany) as string}
                        refreshKey={outlookRefreshKey}
                    />
                </div>
            )}

            {activeTab === 'overview' && (
                <OverviewTab
                    companyCode={companyCode || resolvedCompany}
                    isAdmin={isAdmin}
                />
            )}

            {activeTab === 'accounts' && (
                <BankAccountsTab
                    companyCode={companyCode || resolvedCompany}
                    canEdit={canEdit}
                    syncCheck={syncCheck}
                    onRefreshSyncCheck={refreshSyncCheck}
                />
            )}

            {/* Copy Modal */}
            {showCopyModal && resolvedCompany && (
                <CopyEntriesModal
                    companyCode={resolvedCompany}
                    availableDates={availableDates}
                    onCopy={handleCopy}
                    onClose={() => setShowCopyModal(false)}
                />
            )}
        </div>
    );
}
